/**
 * Splits a cart-level discount across lines without exceeding any line's server-computed
 * ceiling (maxItemDiscount). Port of the Android DiscountAllocator; its tests are the spec.
 *
 * Whole rupiah only (the backend compares against a floored cap). Guarantees for a non-empty cart:
 *   alloc[i] <= floor(cap[i])
 *   sum(alloc) == min(floor(totalDiscount), sum(floor(cap)))
 * A null/undefined cap (server has not answered yet) falls back to the line total.
 */
export type DiscountLine = { unitPrice: number; quantity: number; maxItemDiscount?: number | null };

const lineTotalOf = (i: DiscountLine) => (i.unitPrice || 0) * i.quantity;
const capOf = (i: DiscountLine) => Math.floor(Math.max(0, i.maxItemDiscount ?? lineTotalOf(i)));

/** Total the cart can absorb: the sum of the per-line ceilings. */
export const maxDiscountOf = (items: DiscountLine[]) => items.reduce((s, i) => s + capOf(i), 0);

export function distributeDiscount(items: DiscountLine[], totalDiscount: number): number[] {
    const alloc = items.map(() => 0);
    let remaining = Math.floor(Math.max(0, totalDiscount));
    if (items.length === 0 || remaining <= 0) return alloc;

    const lineTotals = items.map(lineTotalOf);
    const caps = items.map(capOf);

    // Each pass fills the budget or saturates at least one line, so the item count bounds it.
    for (let pass = 0; remaining > 0 && pass <= items.length; pass++) {
        const eligible = items.map((_, i) => i).filter((i) => alloc[i] < caps[i]);
        const base = eligible.reduce((s, i) => s + lineTotals[i], 0);
        if (eligible.length === 0 || base <= 0) break;
        let allocated = 0;
        for (const i of eligible) {
            const take = Math.min(Math.floor((remaining * lineTotals[i]) / base), caps[i] - alloc[i]);
            if (take > 0) { alloc[i] += take; allocated += take; }
        }
        remaining -= allocated;
        if (allocated === 0) break;
    }

    // Sub-rupiah shares never survive the floor; hand the residue to lines with headroom, largest first.
    if (remaining > 0) {
        for (const i of items.map((_, i) => i).sort((a, b) => lineTotals[b] - lineTotals[a])) {
            if (remaining <= 0) break;
            const take = Math.min(caps[i] - alloc[i], remaining);
            if (take > 0) { alloc[i] += take; remaining -= take; }
        }
    }
    return alloc;
}
